import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getReviews, createReview } from '../../services/api';
import { useLang } from '../../i18n/LanguageContext';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import GamifiedButton from '../common/GamifiedButton';

const ReviewsSection = ({ productId }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useAuth();
    const { t } = useLang();
    const navigate = useNavigate();

    useEffect(() => {
        fetchReviews();
    }, [productId]);

    const fetchReviews = async () => {
        try {
            const data = await getReviews(productId);
            setReviews(data.reviews || []);
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReviewSubmit = async (reviewData) => {
        setIsSubmitting(true);
        try {
            await createReview(productId, reviewData);
            await fetchReviews(); // Refresh list
            setShowForm(false);
        } catch (error) {
            alert(error.message || t('avis.section.erreurEnvoi'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleWriteReviewClick = () => {
        if (!user) {
            navigate('/login', {
              state: { from: { pathname: window.location.pathname + window.location.search } },
            });
            return;
        }
        setShowForm(!showForm);
    };

    const averageRating = reviews.length > 0
        ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
        : null;

    // L'accroche doit rester vraie quel que soit le nombre d'avis : annoncer
    // « des centaines de familles » avec un seul avis affiché juste en dessous
    // se voit tout de suite, et nous a déjà valu une alerte Search Console.
    const accroche = reviews.length === 0
        ? t('avis.section.accrocheAucun')
        : reviews.length < 5
            ? t('avis.section.accrochePeu')
            : t('avis.section.accrocheNombreux', { n: reviews.length });

    return (
        <section id="reviews" className="py-25 px-5 md:py-[70px] sm:py-[50px] sm:px-4 bg-gradient-to-b from-white via-[#F7F4EC] to-white relative overflow-hidden before:content-[''] before:absolute before:-top-25 before:-right-[50px] before:w-[400px] before:h-[400px] before:bg-[radial-gradient(circle,rgba(212,165,116,0.08)_0%,transparent_70%)] before:rounded-full before:pointer-events-none after:content-[''] after:absolute after:-bottom-20 after:-left-[30px] after:w-[350px] after:h-[350px] after:bg-[radial-gradient(circle,rgba(255,215,0,0.06)_0%,transparent_70%)] after:rounded-full after:pointer-events-none">
            <div className="max-w-[1400px] mx-auto relative z-10">
                {/* HEADER - Nouvelle disposition */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center mb-16 p-12 lg:p-10 md:p-8 sm:p-6 bg-gradient-to-br from-[#F7F4EC]/80 to-[#4AA294]/5 rounded-[20px] border-2 border-[#4AA294] shadow-[0_8px_32px_rgba(212,165,116,0.1)]">
                    {/* LEFT - Titre + Description */}
                    <div className="flex flex-col gap-4">
                        <div className="inline-flex w-fit bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white px-4 py-2 rounded-2xl text-[0.85rem] font-bold tracking-wide uppercase">{t('avis.section.badge')}</div>
                        <h2 className="text-[2.8rem] lg:text-[2.2rem] md:text-[1.8rem] sm:text-2xl font-black bg-gradient-to-br from-[#33403B] to-[#2F7E72] bg-clip-text text-transparent m-0 font-['Fredoka',_'Poppins',_sans-serif] leading-tight">{t('avis.section.titre')}</h2>
                        <p className="text-[1.05rem] md:text-base sm:text-[0.95rem] text-[#5A5A5A] leading-relaxed m-0 font-medium">
                            {accroche}
                        </p>
                    </div>

                    {/* RIGHT - Stats + CTA */}
                    <div className="flex flex-col gap-6">
                        {/* Stats Card */}
                        <div className="flex items-center justify-center gap-8 bg-white p-8 lg:p-6 md:p-5 sm:p-4 rounded-2xl border-2 border-[#4AA294] shadow-[0_8px_24px_rgba(212,165,116,0.12)] transition-all duration-300 hover:shadow-[0_12px_32px_rgba(212,165,116,0.18)] hover:border-[#EDB82F]">
                            {reviews.length > 0 ? (
                                <>
                                    {/* Rating Display */}
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="text-5xl font-black bg-gradient-to-br from-[#4AA294] to-[#2F7E72] bg-clip-text text-transparent">
                                            {averageRating}
                                        </div>
                                        <div className="flex gap-1 text-2xl">
                                            {'⭐'.repeat(Math.round(averageRating))}
                                        </div>
                                        <div className="text-sm text-gray-600 font-medium">
                                            {reviews.length} {reviews.length > 1 ? t('avis.section.compteurPluriel') : t('avis.section.compteurSingulier')}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-col items-center gap-3 py-4">
                                    <span className="text-5xl">🌟</span>
                                    <p className="text-sm text-gray-600 font-medium text-center">{t('avis.section.premierAvis')}</p>
                                </div>
                            )}
                        </div>

                        {/* CTA Button */}
                        <GamifiedButton
                            variant="primary"
                            size="lg"
                            onClick={handleWriteReviewClick}
                            className="reviews-cta-btn"
                        >
                            {showForm ? t('avis.section.fermer') : t('avis.section.laisserAvis')}
                        </GamifiedButton>
                    </div>
                </div>

                {/* FORM - Full Width if shown */}
                {showForm && (
                    <div className="review-form-wrapper">
                        <ReviewForm onSubmit={handleReviewSubmit} isSubmitting={isSubmitting} />
                    </div>
                )}

                {/* REVIEWS LIST */}
                <div className="reviews-content">
                    {loading ? (
                        <div className="reviews-loading">
                            <div className="loading-spinner"></div>
                            <p>{t('avis.section.chargement')}</p>
                        </div>
                    ) : reviews.length > 0 ? (
                        <>
                            {/* Section Title pour les reviews */}
                            <div className="reviews-list-header">
                                <h3 className="reviews-list-title">{t('avis.section.listeTitre')}</h3>
                            </div>

                            <div className="reviews-grid">
                                {reviews.map((review) => (
                                    <ReviewCard key={review.id} review={review} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="no-reviews">
                            <div className="no-reviews-icon">🌟</div>
                            <h3>{t('avis.section.aucunTitre')}</h3>
                            <p>{t('avis.section.aucunTexte')}</p>
                            <GamifiedButton
                                variant="secondary"
                                size="md"
                                onClick={handleWriteReviewClick}
                            >
                                {t('avis.section.ecrirePremier')}
                            </GamifiedButton>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ReviewsSection;
